import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import useUser from "../../../hooks/useUser";
import {
  getModuleSearchResultsUrl,
  resetModuleSearch,
  SEARCH_RESULTS_LABEL,
  useModuleSearchLocation,
  useModuleSearchSubmitted,
} from "../../../util/usePersistedFilterFormik";

// Record sub-page links are relative and keep the whole query string so the
// search (and prev/next context) survives moving between a record's pages.
export const getMenuItemUrl = ({
  hasSearchResults,
  href,
  isAllLink,
  locationSearch = "",
  searchLocation,
}) => {
  if (isAllLink) {
    return hasSearchResults
      ? getModuleSearchResultsUrl(href, searchLocation)
      : href;
  }
  if (!href?.startsWith("/")) return `${href}${locationSearch}`;
  const status = new URLSearchParams(locationSearch).get("status");
  return `${href}${status && status !== "all" ? `?status=${status}` : ""}`;
};

const MenuItem = props => {
  const { active = false, href, icon, name, open } = props || {};
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const hideTooltipTimer = useRef(null);

  const location = useLocation();
  const isAllLink = /^All /.test(name) && /^\/[^/]+\/?$/.test(href);
  const hasSearchResults = useModuleSearchSubmitted(isAllLink ? href : "");
  const searchLocation = useModuleSearchLocation(isAllLink ? href : "");
  const linkName = isAllLink && hasSearchResults ? SEARCH_RESULTS_LABEL : name;
  const to = getMenuItemUrl({
    hasSearchResults,
    href,
    isAllLink,
    locationSearch: location.search,
    searchLocation,
  });
  const showTooltip = !open && Boolean(linkName) && Boolean(tooltipPosition);

  useEffect(() => {
    return () => {
      if (hideTooltipTimer.current) {
        clearTimeout(hideTooltipTimer.current);
      }
    };
  }, []);

  const handleMouseEnter = event => {
    if (open || !name) return;
    if (hideTooltipTimer.current) {
      clearTimeout(hideTooltipTimer.current);
      hideTooltipTimer.current = null;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      left: bounds.right + 1,
      top: bounds.top + bounds.height / 2,
    });
    requestAnimationFrame(() => {
      setTooltipVisible(true);
    });
  };

  const handleMouseLeave = _event => {
    setTooltipVisible(false);
    hideTooltipTimer.current = setTimeout(() => {
      setTooltipPosition(null);
      hideTooltipTimer.current = null;
    }, 120);
  };

  return (
    <>
      <Link
        className="group flex h-5 cursor-pointer items-center"
        to={to}
        onClick={event => {
          if (
            isAllLink &&
            !hasSearchResults &&
            !event.metaKey &&
            !event.ctrlKey &&
            !event.shiftKey &&
            !event.altKey &&
            event.button === 0
          ) {
            resetModuleSearch(href);
          }
        }}
        data-title={linkName}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onBlur={handleMouseLeave}
      >
        <div className="flex w-16 flex-shrink-0 items-center justify-center">
          {icon && (
            <div
              className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border transition-colors duration-150 ${
                active
                  ? "border-black bg-black text-white"
                  : "border-transparent text-gray-500 group-hover:border-black group-hover:bg-black group-hover:text-white"
              }`}
            >
              <props.icon className="h-4 w-4" aria-hidden="true" />
            </div>
          )}
        </div>
        {open && (
          <span
            className={`relative -left-[5px] flex-1 whitespace-nowrap text-sm ${active ? "text-black underline" : "text-gray-500"}`}
          >
            {linkName}
          </span>
        )}
      </Link>
      {showTooltip &&
        createPortal(
          <div
            className={`pointer-events-none fixed z-[100] -translate-y-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white shadow transition-all duration-150 ${
              tooltipVisible ? "opacity-100" : "opacity-0"
            }`}
            style={{
              left: tooltipPosition.left,
              top: tooltipPosition.top,
            }}
          >
            {linkName}
          </div>,
          document.body,
        )}
    </>
  );
};

const NavBar = props => {
  const { navigation = [], open, path } = props || {};
  const location = useLocation();
  const { hasPermission } = useUser();
  const { pathname } = location || {};

  return (
    <div
      className={`no-scrollbar flex h-full max-h-full flex-initial flex-col overflow-y-auto bg-white py-5 ${
        open ? "mr-5 w-fit min-w-[12rem] max-w-xs" : "w-16"
      } min-h-full overflow-x-hidden`}
    >
      <nav className="flex flex-1 flex-col gap-6 bg-white" aria-label="Sidebar">
        {navigation?.map((item, index) => {
          const { permissions } = item;
          const hasPermissions = permissions
            ? permissions?.every?.(permission => hasPermission(permission))
            : true;
          const active = path
            ? path?.includes(item?.href)
            : pathname?.split("/")?.pop() === item?.href ||
              pathname?.split("/")?.[2] === item?.href; // Admin Module User and Roles
          return (
            hasPermissions && (
              <MenuItem key={index} {...item} open={open} active={active} />
            )
          );
        })}
      </nav>
    </div>
  );
};

const SimpleSidebar = props => {
  const { onEntriesChange, open, setOpen } = props || {};
  const [hasSidebarEntries, setHasSidebarEntries] = useState(false);
  const sidebarTargetRef = useRef(null);

  useEffect(() => {
    if (!sidebarTargetRef.current) return undefined;

    const updateHasEntries = () => {
      setHasSidebarEntries(sidebarTargetRef.current?.children?.length > 0);
    };

    updateHasEntries();

    const observer = new MutationObserver(updateHasEntries);
    observer.observe(sidebarTargetRef.current, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    onEntriesChange?.(hasSidebarEntries);
  }, [hasSidebarEntries, onEntriesChange]);

  return (
    <>
      {open && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex max-h-[100dvh] w-[min(20rem,85vw)] flex-shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-white shadow-xl transition-transform duration-200 ease-out lg:static lg:z-auto lg:h-screen lg:w-auto lg:translate-x-0 lg:shadow-none ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-label="Application navigation"
      >
        <button
          type="button"
          className={`flex h-16 flex-shrink-0 items-center border-b border-gray-200 bg-white p-5 text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 ${
            hasSidebarEntries
              ? "cursor-pointer hover:bg-gray-100 hover:text-gray-500"
              : "cursor-default"
          }`}
          onClick={() => {
            if (hasSidebarEntries) {
              setOpen(!open);
            }
          }}
          disabled={!hasSidebarEntries}
          aria-label={open ? "Close side bar" : "Open side bar"}
          aria-expanded={open}
        >
          {hasSidebarEntries ? (
            open ? (
              <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
            ) : (
              <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
            )
          ) : (
            <span className="block h-6 w-6" aria-hidden="true" />
          )}
        </button>
        <div
          id="sidebar"
          ref={sidebarTargetRef}
          className="no-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
        />
      </aside>
    </>
  );
};

export { NavBar, SimpleSidebar };
